import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { fetchThingsToDoByMonthList } from "@/services/api/blog";
import {
  BLOG_MONTH_SECTION_ID,
  normalizeThingsToDoByMonthList,
} from "@/utils/blogHelpers";
import { scrollToSectionById } from "@/utils/scrollToSection";
import { withRegionPath } from "@/utils/session";
import styles from "./ThingsToDoMonth.module.css";

export default function ThingsToDoMonth({ initialMonths = null }) {
  const [months, setMonths] = useState(initialMonths || []);
  const [loading, setLoading] = useState(!initialMonths?.length);

  useEffect(() => {
    if (initialMonths?.length) {
      setMonths(initialMonths);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchThingsToDoByMonthList()
      .then((response) => {
        if (cancelled) return;
        setMonths(normalizeThingsToDoByMonthList(response));
      })
      .catch(() => {
        if (!cancelled) setMonths([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [initialMonths]);

  useEffect(() => {
    if (loading || typeof window === "undefined") return undefined;
    if (window.location.hash !== `#${BLOG_MONTH_SECTION_ID}`) return undefined;

    const timer = window.setTimeout(() => {
      scrollToSectionById(BLOG_MONTH_SECTION_ID);
    }, 50);

    return () => window.clearTimeout(timer);
  }, [loading]);

  const visibleMonths = useMemo(() => months.filter((item) => item.month), [months]);

  if (!loading && !visibleMonths.length) {
    return null;
  }

  return (
    <section
      id={BLOG_MONTH_SECTION_ID}
      className={`${styles.blogsecMonthSection} standard-padding`}
    >
      <div className={styles.blogsecMonthHeader}>
        <h2 className="standard-heading">What&apos;s On, Month By Month </h2>
        <p className="standard-subheading">
          From season to season, find the perfect trip for every month.
        </p>
      </div>

      {loading ? (
        <div className={styles.blogsecMonthLoading}>
          <img src="/assets/loading.gif" alt="Loading months" width={48} height={48} />
        </div>
      ) : (
        <div className={styles.blogsecMonthGrid}>
          {visibleMonths.map((item) => {
            const disabled = !Number(item.count);
            const href = withRegionPath(`/blog/month/${item.slug}`);

            if (disabled) {
              return (
                <div
                  key={item.id || item.slug}
                  className={`${styles.blogsecMonthCard} ${styles.blogsecMonthCardDisabled}`}
                  aria-disabled="true"
                >
                  <MonthCardContent item={item} />
                </div>
              );
            }

            return (
              <Link
                key={item.id || item.slug}
                href={href}
                className={styles.blogsecMonthCard}
              >
                <MonthCardContent item={item} />
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

function MonthCardContent({ item }) {
  return (
    <>
      <div className={styles.blogsecMonthLeft}>
        <div className={styles.blogsecMonthIconWrap}>
          <img
            src={item.icon}
            alt={item.month}
            className={styles.blogsecMonthImg}
          />
        </div>
        <span className={styles.blogsecMonthTitle}>{item.month}</span>
      </div>

      <svg
        className={styles.blogsecMonthArrow}
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#333333"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </svg>
    </>
  );
}
